import React from 'react';

/** Google-Meet-style control bar: mic · camera · screen-share · (mirror toggle) · captions · end. */
const Btn = ({ title, description, onClick, disabled, tone = 'default', children }) => {
  const tones = {
    default: 'bg-slate-100 text-slate-700 hover:bg-slate-200 ring-1 ring-black/5 dark:bg-white/5 dark:text-slate-100 dark:hover:bg-white/10 dark:ring-white/10',
    active: 'bg-violet-600 text-white hover:bg-violet-500 shadow-lg shadow-violet-600/30',
    danger: 'bg-rose-600 text-white hover:bg-rose-500 shadow-lg shadow-rose-600/30',
    end: 'bg-rose-600 text-white hover:bg-rose-500 shadow-lg shadow-rose-600/30',
  };
  return (
    <button
      type="button"
      title={description ? `${title}. ${description}` : title}
      aria-label={title}
      aria-description={description}
      onClick={onClick}
      disabled={disabled}
      className={`grid h-12 w-12 place-items-center rounded-full transition active:scale-95 disabled:opacity-40 ${tones[tone]}`}
    >
      {children}
    </button>
  );
};

const I = {
  mic: (off) => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="2.5" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
      <line x1="12" y1="17.5" x2="12" y2="21.5" />
      <line x1="8.5" y1="21.5" x2="15.5" y2="21.5" />
      {off && <line x1="3.5" y1="3.5" x2="20.5" y2="20.5" />}
    </svg>
  ),
  cam: (off) => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="6.5" width="13" height="11" rx="2.5" />
      <path d="M15.5 10.5l5-3v9l-5-3z" />
      {off && <line x1="3.5" y1="3.5" x2="20.5" y2="20.5" />}
    </svg>
  ),
  share: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="13" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <path d="M12 13.5V8M9.5 10.5L12 8l2.5 2.5" />
    </svg>
  ),
  eye: (hidden) => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      {hidden
        ? <><path d="M10.6 6.1A9.5 9.5 0 0 1 12 6c6.5 0 10 6 10 6a13.4 13.4 0 0 1-2.4 2.95M6.3 6.3A13.4 13.4 0 0 0 2 12s3.5 6 10 6a9.4 9.4 0 0 0 3.7-.75" /><line x1="3.5" y1="3.5" x2="20.5" y2="20.5" /></>
        : <><path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12z" /><circle cx="12" cy="12" r="2.7" /></>}
    </svg>
  ),
  cc: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="5" width="19" height="14" rx="3" />
      <line x1="6.5" y1="11" x2="11" y2="11" />
      <line x1="13" y1="11" x2="17.5" y2="11" />
      <line x1="6.5" y1="14.5" x2="14" y2="14.5" />
    </svg>
  ),
  end: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
      <path d="M12 9.5c-1.7 0-3.34.26-4.87.74-.34.1-.58.42-.58.79v2.41c0 .35-.22.67-.55.8-.83.32-1.6.74-2.3 1.25-.37.27-.89.23-1.2-.09l-1.4-1.4a.86.86 0 0 1 .03-1.25C3.84 10.66 7.66 9 12 9s8.16 1.66 11.34 4.55c.36.32.38.88.03 1.24l-1.4 1.4c-.31.32-.83.36-1.2.09a11.7 11.7 0 0 0-2.3-1.25.86.86 0 0 1-.55-.8v-2.4c0-.38-.24-.7-.58-.8A16.6 16.6 0 0 0 12 9.5z" />
    </svg>
  ),
};

const MeetingControls = ({
  muted, onToggleMute, localSpeechMonitoring = false,
  cameraOn, onToggleCamera,
  sharing, onToggleShare,
  hideMirror, onToggleHideMirror,
  captionsOpen, onToggleCaptions,
  onEnd, disabled,
}) => (
  <div className="flex items-center gap-2.5 rounded-full bg-white/90 px-3 py-2 shadow-xl ring-1 ring-black/5 backdrop-blur dark:bg-slate-900/70 dark:ring-white/10 sm:gap-3">
    <Btn title={muted ? 'Unmute microphone' : 'Mute microphone'}
      description={muted && localSpeechMonitoring ? 'Microphone remains active for local reminders. Muted speech stays on this device.' : undefined}
      onClick={onToggleMute} disabled={disabled} tone={muted ? 'danger' : 'default'}>
      {I.mic(muted)}
    </Btn>
    <Btn title={cameraOn ? 'Turn camera off' : 'Turn camera on'} onClick={onToggleCamera} disabled={disabled} tone={cameraOn ? 'default' : 'danger'}>
      {I.cam(!cameraOn)}
    </Btn>
    <Btn title={sharing ? 'Stop sharing screen' : 'Share your screen'} onClick={onToggleShare} disabled={disabled} tone={sharing ? 'active' : 'default'}>
      {I.share}
    </Btn>
    {sharing && (
      <Btn title={hideMirror ? 'Show shared screen' : 'Hide shared screen (avoid mirror)'} onClick={onToggleHideMirror} disabled={disabled} tone={hideMirror ? 'active' : 'default'}>
        {I.eye(hideMirror)}
      </Btn>
    )}
    <Btn title={captionsOpen ? 'Hide captions' : 'Show captions'} onClick={onToggleCaptions} tone={captionsOpen ? 'active' : 'default'}>
      {I.cc}
    </Btn>
    <Btn title="Leave the interview" onClick={onEnd} disabled={disabled} tone="end">
      {I.end}
    </Btn>
  </div>
);

export default MeetingControls;
